// client/src/pages/ProfileHub.js
// ✅ asset-ready gate + loader overlay + fade-in (matches NexusHub/GenesisHub pattern)

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./Dashboard.css"; // for core-tile / cortex-beta-tag base styles
import "./ProfileHub.css";
import Loading from "../components/Loading";
import CphereHeaderLogo from "../components/CphereHeaderLogo";

import backgroundImage from "../assets/background.jpg";

import notificationsImage from "../assets/tiles/notifications.tile.gif"; // animated — 28 frames
import settingsImage from "../assets/tiles/settings.tile.jpg";
import appImage from "../assets/app.jpg";
import billingImage from "../assets/tiles/billing.tile.jpg";

/* ✅ module-level cache so returning to ProfileHub is smooth */
let CLIENT_PROFILEHUB_ASSETS_WARM = false;

function preloadImage(url, timeoutMs = 4500) {
  return new Promise((resolve) => {
    if (!url) return resolve();

    const img = new Image();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };

    const timer = setTimeout(finish, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      if (typeof img.decode === "function") {
        img.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };

    img.onerror = () => {
      clearTimeout(timer);
      finish();
    };

    img.src = url;
  });
}

const ProfileHub = () => {
  const navigate = useNavigate();

  const [assetsReady, setAssetsReady] = useState(CLIENT_PROFILEHUB_ASSETS_WARM);
  const [fadeIn, setFadeIn] = useState(false);
  const [uiReady, setUiReady] = useState(CLIENT_PROFILEHUB_ASSETS_WARM);

  const uiReadyTimeoutRef = useRef(null);

  const handleLogoClick = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    if (CLIENT_PROFILEHUB_ASSETS_WARM) {
      setAssetsReady(true);
      setUiReady(true);
      return;
    }

    const urls = [
      backgroundImage,
      notificationsImage,
      settingsImage,
      appImage,
      billingImage,
    ];

    (async () => {
      try {
        await Promise.all(urls.map((u) => preloadImage(u)));
      } finally {
        if (cancelled) return;
        CLIENT_PROFILEHUB_ASSETS_WARM = true;
        setAssetsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!assetsReady) {
      setFadeIn(false);
      setUiReady(false);
      return;
    }

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFadeIn(true));
    });

    if (uiReadyTimeoutRef.current) clearTimeout(uiReadyTimeoutRef.current);
    uiReadyTimeoutRef.current = setTimeout(() => {
      setUiReady(true);
    }, 450);

    return () => {
      cancelAnimationFrame(raf);
      if (uiReadyTimeoutRef.current) clearTimeout(uiReadyTimeoutRef.current);
    };
  }, [assetsReady]);

  const handleReadyShellTransitionEnd = useCallback((e) => {
    if (e?.propertyName !== "opacity") return;
    setUiReady(true);
  }, []);

  return (
    <div className={`profilehub-page-container ${uiReady ? "profilehub-is-ready" : ""}`}>
      {/* ✅ Loader stays mounted UNTIL the UI is visibly faded-in */}
      {!uiReady && (
        <div className="profilehub-loading-shell">
          <Loading />
        </div>
      )}

      {/* ✅ Ready shell is mounted immediately but starts hidden (opacity:0) */}
      <div
        className={`profilehub-ready-shell ${fadeIn ? "profilehub-fade-in" : ""}`}
        aria-hidden={!assetsReady}
        onTransitionEnd={handleReadyShellTransitionEnd}
      >
        <div className="profilehub-page-content">
          {/* Shared Cphere header logo – returns to main client dashboard */}
          <CphereHeaderLogo
            alt="Cphere"
            className="profilehub-poweredby-logo"
            onClick={handleLogoClick}
            style={{ cursor: "pointer" }}
          />

          {/* Hero – CONTROL / CENTER, cortex hero spec */}
          <div className="profilehub-hero-container">
            <div className="profilehub-hero-pill">
              <span className="profilehub-hero-pill-label cphere-hero-pill-wordmark">CONTROL</span>
              <span className="profilehub-hero-pill-tag cphere-hero-pill-tag">CENTER</span>
            </div>
            <div className="profilehub-hero-shimmer-bar" />
          </div>

          {/* Tiles */}
          <div className="profilehub-tile-grid">
            {/* Profile Settings */}
            <div
              className="profilehub-tile"
              onClick={() => navigate("/settings")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/settings");
                }
              }}
            >
              <div
                className="profilehub-tile-face"
                style={{ backgroundImage: `url(${settingsImage})` }}
              >
                <span className="profilehub-tile-label">Profile</span>
              </div>
            </div>

            {/* Billing */}
            <div
              className="profilehub-tile"
              onClick={() => navigate("/billing")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/billing");
                }
              }}
            >
              <div
                className="profilehub-tile-face"
                style={{ backgroundImage: `url(${billingImage})` }}
              >
                <span className="profilehub-tile-label">Billing</span>
              </div>
            </div>

            {/* Notifications */}
            <div
              className="profilehub-tile"
              onClick={() => navigate("/notifications")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/notifications");
                }
              }}
            >
              <div
                className="profilehub-tile-face"
                style={{ backgroundImage: `url(${notificationsImage})` }}
              >
                <span className="profilehub-tile-label">Notifications</span>
              </div>
            </div>

            {/* Install App */}
            <div
              className="profilehub-tile"
              onClick={() => navigate("/install-app")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/install-app");
                }
              }}
            >
              <div
                className="profilehub-tile-face"
                style={{ backgroundImage: `url(${appImage})` }}
              >
                <span className="cortex-beta-tag">APP</span>
                <span className="profilehub-tile-label">Install</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHub;
